import { Mail, Linkedin, Instagram } from 'lucide-react';
import type { Page } from '../App';

interface FooterProps {
  navigate: (page: Page) => void;
}

export default function Footer({ navigate }: FooterProps) {
  return (
    <footer className="bg-gray-900 text-gray-400" role="contentinfo" aria-label="Site footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-wrap gap-3 mb-10" aria-label="Advertising platforms">
          {[
            { label: 'Meta Ads', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
            { label: 'X Ads', color: 'bg-sky-500/10 text-sky-400 border-sky-500/20' },
            { label: 'TikTok Ads', color: 'bg-pink-500/10 text-pink-400 border-pink-500/20' },
            { label: 'Google Ads', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
          ].map(({ label, color }) => (
            <span key={label} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${color} tracking-wide`}>
              {label}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <button
              onClick={() => navigate('home')}
              className="mb-4 inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-lg"
              aria-label="Go to homepage"
            >
              <img src="/logo.png" alt="HybridAds.ai" className="h-[120px] w-auto brightness-0 invert" width="120" height="120" loading="lazy" decoding="async" />
            </button>
            <p className="text-sm leading-relaxed max-w-sm mt-2">
              AI Systems Integrators building intelligent automation for modern businesses — reducing digital labor costs and driving measurable returns across every channel.
            </p>
            <div className="mt-4 text-sm">
              <a
                href="mailto:hello@hybridads.ai"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                aria-label="Email us at hello@hybridads.ai"
              >
                <Mail className="h-4 w-4" aria-hidden="true" />
                hello@hybridads.ai
              </a>
            </div>
            <div className="flex space-x-4 mt-5" aria-label="Social media links">
              <a href="https://twitter.com/hybridadsai" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded" aria-label="Follow us on X (Twitter)">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/hybridadsai" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded" aria-label="Connect on LinkedIn">
                <Linkedin className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href="https://www.instagram.com/hybridadsai" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded" aria-label="Follow us on Instagram">
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          <nav aria-label="Site navigation">
            <h4 className="text-white font-semibold mb-4">Navigation</h4>
            <ul className="space-y-2 text-sm">
              <li><button onClick={() => navigate('home')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">Home</button></li>
              <li><button onClick={() => navigate('ai-agency')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">AI Systems</button></li>
              <li>
                <button onClick={() => navigate('nemo-claw')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white inline-flex items-center gap-2">
                  AI Agents
                  <span className="text-xs bg-emerald-600 text-white font-bold px-1.5 py-0.5 rounded-full leading-none">New</span>
                </button>
              </li>
              <li><button onClick={() => navigate('ai-visibility')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">AI Visibility</button></li>
              <li>
                <button onClick={() => navigate('gtm-service')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white inline-flex items-center gap-2">
                  AI Sales Team
                  <span className="text-xs bg-amber-500 text-white font-bold px-1.5 py-0.5 rounded-full leading-none">Beta</span>
                </button>
              </li>
              <li>
                <button onClick={() => navigate('content-lab')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white inline-flex items-center gap-2">
                  AI Content Lab
                  <span className="text-xs bg-amber-500 text-white font-bold px-1.5 py-0.5 rounded-full leading-none">Beta</span>
                </button>
              </li>
              <li><button onClick={() => navigate('ai-score')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">AI Score</button></li>
              <li><button onClick={() => navigate('dashboard')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">Ad Performance</button></li>
              <li><button onClick={() => navigate('about')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">About Us</button></li>
              <li><button onClick={() => navigate('case-studies')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">Case Studies</button></li>
              <li><button onClick={() => navigate('partners')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">Partners</button></li>
              <li><button onClick={() => navigate('social-generator')} className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">Social Cards</button></li>
              <li><a href="https://calendly.com/hybridadsai" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white">Book a Call</a></li>
            </ul>
          </nav>

          <div>
            <h4 className="text-white font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-sm">
              <li>Google Ads Management</li>
              <li>Meta / Facebook Ads</li>
              <li>TikTok Advertising</li>
              <li>Shopify & E-Commerce</li>
              <li>AI-Powered Optimization</li>
              <li><button onClick={() => navigate('ai-agency')} className="hover:text-white transition-colors">SaaS Platforms</button></li>
              <li><button onClick={() => navigate('ai-agency')} className="hover:text-white transition-colors">Conversational AI</button></li>
              <li><button onClick={() => navigate('ai-agency')} className="hover:text-white transition-colors">Intelligent Search & RAG</button></li>
              <li><button onClick={() => navigate('ai-agency')} className="hover:text-white transition-colors">On-Device & Edge AI</button></li>
              <li><button onClick={() => navigate('ai-agency')} className="hover:text-white transition-colors">Autonomous Agents</button></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center text-sm gap-4">
          <p>© {new Date().getFullYear()} HybridAds.ai. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <button
              onClick={() => navigate('privacy')}
              className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => navigate('terms')}
              className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white"
            >
              Terms of Service
            </button>
            <a
              href="mailto:dmca@hybridads.ai"
              className="hover:text-white transition-colors focus-visible:outline-none focus-visible:text-white"
            >
              DMCA
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
